import { LitElement, html } from "lit";
import { query, state, property, customElement } from "lit/decorators.js";
import { classMap } from "lit/directives/class-map.js";
import { ifDefined } from "lit/directives/if-defined.js";
import { hasSlot } from "../common/internal/slot.js";
import styles from "./button.styles.js";
import "../spinner/index.js";
var __defProp = Object.defineProperty;
var __getOwnPropDesc = Object.getOwnPropertyDescriptor;
var __decorateClass = (decorators, target, key, kind) => {
  var result = kind > 1 ? void 0 : kind ? __getOwnPropDesc(target, key) : target;
  for (var i = decorators.length - 1, decorator; i >= 0; i--)
    if (decorator = decorators[i])
      result = (kind ? decorator(target, key, result) : decorator(result)) || result;
  if (kind && result)
    __defProp(target, key, result);
  return result;
};
let Button = class extends LitElement {
  constructor() {
    super(...arguments);
    this.hasFocus = false;
    this.hasLabel = false;
    this.type = "default";
    this.color = "secondary";
    this.size = "large";
    this.backgroundMode = "blend";
    this.disabled = false;
    this.focusable = false;
    this.fullWidth = false;
    this.loading = false;
    this.active = false;
    this.circle = false;
    this.submit = false;
  }
  connectedCallback() {
    super.connectedCallback();
    this.handleSlotChange();
  }
  click() {
    this.button.click();
  }
  focus(options) {
    this.button.focus(options);
  }
  blur() {
    this.button.blur();
  }
  handleSlotChange() {
    this.hasLabel = hasSlot(this);
  }
  handleBlur() {
    this.hasFocus = false;
  }
  handleFocus() {
    this.hasFocus = true;
  }
  handleClick(event) {
    if (this.disabled || this.loading) {
      event.preventDefault();
      event.stopPropagation();
    }
  }
  render() {
    if (this.backgroundMode === "solid") {
      this.classList.add("button--solid-background");
    } else {
      this.classList.remove("button--solid-background");
    }
    const isLink = this.href ? true : false;
    const icon = this.type !== "default" ? html`<span part="icon" class="button__icon ${classMap({
      [`button__${this.type}`]: this.type !== "icon"
    })}"><slot @slotchange="${this.handleSlotChange}" name="icon"></slot></span>` : "";
    const spinnerColor = ["secondary", "bright"].includes(this.color) ? "primary" : "bright";
    const spinnerSize = this.size === "small" ? "x-small" : "small";
    const content = this.type === "icon" ? icon : html`${this.type === "icon-left" ? icon : ""} <span part="label" class="button__label"><slot @slotchange="${this.handleSlotChange}"></slot></span>${this.type === "icon-right" ? icon : ""}`;
    const interior = html`${content} ${this.loading ? html`<elm-spinner color="${spinnerColor}" size="${spinnerSize}" isSpinning></elm-spinner>` : ""}`;
    const button = html`<button part="base" class="${classMap({
      button: true,
      [`button--${this.color}`]: true,
      "button--small": this.size === "small",
      "button--large": this.size === "large",
      "button--circle": this.circle,
      "button--disabled": this.disabled,
      "button--focused": this.hasFocus && this.focusable,
      "button--focusable": this.focusable,
      "button--loading": this.loading,
      "button--active": this.active,
      "button--has-label": this.hasLabel,
      "button--has-icon": this.type !== "default",
      "button--has-icon-left": this.type === "icon-left",
      "button--has-icon-right": this.type === "icon-right",
      "button--icon-button": this.type === "icon"
    })}" ?disabled="${this.disabled}" type="${this.submit ? "submit" : "button"}" name="${ifDefined(this.name)}" value="${ifDefined(this.value)}" @blur="${this.handleBlur}" @focus="${this.handleFocus}" @click="${this.handleClick}">${interior}</button>`;
    const link = html`<a ref="${(element) => this.button = element}" part="base" class="${classMap({
      button: true,
      [`button--${this.color}`]: true,
      "button--small": this.size === "small",
      "button--large": this.size === "large",
      "button--circle": this.circle,
      "button--disabled": this.disabled,
      "button--focused": this.hasFocus && this.focusable,
      "button--focusable": this.focusable,
      "button--loading": this.loading,
      "button--active": this.active,
      "button--has-label": this.hasLabel,
      "button--has-icon": this.type !== "default",
      "button--has-icon-left": this.type === "icon-left",
      "button--has-icon-right": this.type === "icon-right"
    })}" href="${ifDefined(this.href)}" target="${ifDefined(this.target)}" download="${ifDefined(this.download)}" rel="${ifDefined(this.target ? "noreferrer noopener" : void 0)}" role="button" aria-disabled="${this.disabled ? "true" : "false"}" tabindex="${this.disabled ? "-1" : "0"}" @blur="${this.handleBlur}" @focus="${this.handleFocus}" @click="${this.handleClick}">${interior}</a>`;
    return isLink ? link : button;
  }
};
Button.styles = styles;
__decorateClass([
  query(".button")
], Button.prototype, "button", 2);
__decorateClass([
  state()
], Button.prototype, "hasFocus", 2);
__decorateClass([
  state()
], Button.prototype, "hasLabel", 2);
__decorateClass([
  property({ reflect: true })
], Button.prototype, "type", 2);
__decorateClass([
  property({ reflect: true })
], Button.prototype, "color", 2);
__decorateClass([
  property({ reflect: true })
], Button.prototype, "size", 2);
__decorateClass([
  property({ reflect: true })
], Button.prototype, "backgroundMode", 2);
__decorateClass([
  property({ type: Boolean, reflect: true })
], Button.prototype, "disabled", 2);
__decorateClass([
  property({ type: Boolean, reflect: true })
], Button.prototype, "focusable", 2);
__decorateClass([
  property({ type: Boolean, reflect: true })
], Button.prototype, "fullWidth", 2);
__decorateClass([
  property({ type: Boolean, reflect: true })
], Button.prototype, "loading", 2);
__decorateClass([
  property({ type: Boolean, reflect: true })
], Button.prototype, "active", 2);
__decorateClass([
  property({ type: Boolean, reflect: true })
], Button.prototype, "circle", 2);
__decorateClass([
  property({ type: Boolean, reflect: true })
], Button.prototype, "submit", 2);
__decorateClass([
  property()
], Button.prototype, "name", 2);
__decorateClass([
  property()
], Button.prototype, "value", 2);
__decorateClass([
  property()
], Button.prototype, "href", 2);
__decorateClass([
  property()
], Button.prototype, "target", 2);
__decorateClass([
  property()
], Button.prototype, "download", 2);
Button = __decorateClass([
  customElement("elm-button")
], Button);
export { Button as default };
